import { LinkMarkdown } from "./LinkMarkdown";
import { cn } from "@/lib/utils";
import { marked } from "marked";
import { memo, useId, useMemo } from "react";
import ReactMarkdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
// import { ButtonCopy } from "../chat/components/ButtonCopy"
import { CodeBlock, CodeBlockCode, CodeBlockGroup } from "./code-block";

export type MarkdownProps = {
  children: string;
  id?: string;
  className?: string;
  components?: Partial<Components>;
};

function parseMarkdownIntoBlocks(markdown: unknown): string[] {
  // Ensure input is string
  const src = typeof markdown === "string" ? markdown : String(markdown || "");
  const tokens = marked.lexer(src);
  return tokens.map((token) => token.raw || "");
}

function extractLanguage(className?: string): string {
  if (!className) return "plaintext";
  const match = className.match(/language-(\w+)/);
  return match ? match[1] : "plaintext";
}

const INITIAL_COMPONENTS: Partial<Components> = {
  code: function CodeComponent({ className, children, ...props }) {
    const isInline =
      !props.node?.position?.start.line ||
      props.node?.position?.start.line === props.node?.position?.end.line;

    if (isInline) {
      return (
        <span
          className={cn(
            "bg-gray-100 text-gray-800 rounded px-1.5 py-0.5 text-sm font-normal",
            className
          )}
          {...props}
        >
          {children}
        </span>
      );
    }

    // Don't render code blocks at all
    return null;
  },
  pre: function PreComponent({ children }) {
    return <>{children}</>;
  },
  a: function AComponent({ href, children, ...props }) {
    if (!href) return <span {...props}>{children}</span>;

    return (
      <LinkMarkdown href={href} {...props}>
        {children}
      </LinkMarkdown>
    );
  },
  hr: function HRComponent(props) {
    return <hr className="my-6 border-t border-gray-200" {...props} />;
  },
  // Add list components
  ul: function ULComponent({ children, ...props }) {
    return (
      <ul className="mb-4 pl-8 list-disc" {...props}>
        {children}
      </ul>
    );
  },
  ol: function OLComponent({ children, ...props }) {
    return (
      <ol className="mb-4 pl-8 list-decimal" {...props}>
        {children}
      </ol>
    );
  },
  li: function LIComponent({ children, ...props }) {
    return (
      <li className="mb-1 break-words" {...props}>
        {children}
      </li>
    );
  },
  // Add table components
  table: function TableComponent({ children, ...props }) {
    return (
      <div className="overflow-x-auto my-4 w-full">
        <table className="w-full table-auto border-collapse text-sm" {...props}>
          {children}
        </table>
      </div>
    );
  },
  thead: function TheadComponent({ children, ...props }) {
    return (
      <thead className="bg-gray-100" {...props}>
        {children}
      </thead>
    );
  },
  tbody: function TbodyComponent({ children, ...props }) {
    return <tbody {...props}>{children}</tbody>;
  },
  tr: function TrComponent({ children, ...props }) {
    return (
      <tr className="border-b border-gray-200" {...props}>
        {children}
      </tr>
    );
  },
  th: function ThComponent({ children, ...props }) {
    return (
      <th
        className="px-4 py-2 text-left font-semibold whitespace-nowrap"
        {...props}
      >
        {children}
      </th>
    );
  },
  td: function TdComponent({ children, ...props }) {
    return (
      <td className="px-4 py-2 whitespace-nowrap" {...props}>
        {children}
      </td>
    );
  },
};

const MemoizedMarkdownBlock = memo(
  function MarkdownBlock({
    content,
    components = INITIAL_COMPONENTS,
  }: {
    content: string;
    components?: Partial<Components>;
  }) {
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    );
  },
  function propsAreEqual(prevProps, nextProps) {
    return prevProps.content === nextProps.content;
  }
);

MemoizedMarkdownBlock.displayName = "MemoizedMarkdownBlock";

function MarkdownComponent({
  children,
  id,
  className,
  components = INITIAL_COMPONENTS,
}: MarkdownProps) {
  const generatedId = useId();
  const blockId = id ?? generatedId;
  const blocks = useMemo(() => parseMarkdownIntoBlocks(children), [children]);

  return (
    <div className={className}>
      {blocks.map((block, index) => (
        <MemoizedMarkdownBlock
          key={`${blockId}-block-${index}`}
          content={block}
          components={components}
        />
      ))}
    </div>
  );
}

const Markdown = memo(MarkdownComponent);
Markdown.displayName = "Markdown";

export { Markdown };
